import { translations } from '../locales/translations';

/**
 * Browser notification permission helpers (CRCN-832).
 *
 * The login flow must never await the browser permission prompt: in Chromium
 * `Notification.requestPermission()` never settles while the prompt is left
 * unanswered, which used to stall the login API and leave the app on an
 * infinite spinner. Everything exported here resolves within a bounded time
 * and never throws, so no caller can be blocked by it.
 */

export type TPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export type TPermissionRequestResult = TPermissionState | 'timeout';

export type TBrowserKey =
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'safari'
  | 'iosSafari'
  | 'inAppBrowser'
  | 'other';

/**
 * The prompt itself has no deadline; this only bounds how long the dialog shows
 * a spinner. A late answer is still picked up by `subscribeToPermissionChange`.
 */
export const PERMISSION_REQUEST_TIMEOUT_MS = 20000;

const hasNotificationApi = (): boolean => typeof window !== 'undefined' && 'Notification' in window;

/**
 * Web push needs all three: the Notification API to ask, a service worker to
 * receive in the background, and PushManager to subscribe. iOS Safari outside
 * an installed PWA has no PushManager, so it fails here.
 */
export const isPushSupported = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return hasNotificationApi() && 'serviceWorker' in navigator && 'PushManager' in window;
};

const normalizePermission = (value?: string): TPermissionState => {
  if (value === 'granted' || value === 'denied') return value;
  return 'default';
};

export const getPermissionState = (): TPermissionState => {
  if (!isPushSupported()) return 'unsupported';
  return normalizePermission(Notification.permission);
};

/**
 * Asks the browser for permission without ever hanging.
 *
 * Resolves to 'timeout' when the prompt is ignored, which is the case that
 * used to freeze login. Returns the current state untouched when the prompt
 * cannot be shown again (already granted or already denied).
 */
export const requestPermissionSafely = async (
  timeoutMs: number = PERMISSION_REQUEST_TIMEOUT_MS,
): Promise<TPermissionRequestResult> => {
  if (!isPushSupported()) return 'unsupported';

  const current = getPermissionState();
  if (current !== 'default') return current;

  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    const ask = new Promise<TPermissionState>((resolve) => {
      try {
        // Legacy Safari only supports the callback form, modern browsers return
        // a promise. Support both rather than assuming either.
        const maybePromise = (Notification as any).requestPermission((result: string) =>
          resolve(normalizePermission(result)),
        );
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise
            .then((result: string) => resolve(normalizePermission(result)))
            .catch(() => resolve('default'));
        }
      } catch (error) {
        console.error('Notification.requestPermission failed: ', error);
        resolve('default');
      }
    });

    const timeout = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), timeoutMs);
    });

    return await Promise.race([ask, timeout]);
  } catch (error) {
    console.error('requestPermissionSafely failed: ', error);
    return 'default';
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Notifies on permission changes that happen outside our own request: a late
 * answer to a prompt we already timed out on, or the user flipping the setting
 * in a browser settings screen and coming back to the tab.
 */
/**
 * Where a permission update came from.
 *
 * 'event'   - the Permissions API reported an actual change, so the user
 *             really did alter the setting.
 * 'recheck' - we simply re-read the value (on focus, or on navigation). The
 *             value may be unchanged, and in some browsers it is not even
 *             stable, so callers must not treat it as a deliberate change.
 */
export type TPermissionChangeSource = 'event' | 'recheck';

export const subscribeToPermissionChange = (
  onChange: (state: TPermissionState, source: TPermissionChangeSource) => void,
): (() => void) => {
  const cleanups: Array<() => void> = [];

  try {
    const permissions = (navigator as any)?.permissions;
    if (permissions?.query) {
      permissions
        .query({ name: 'notifications' })
        .then((status: any) => {
          const handler = () => onChange(getPermissionState(), 'event');
          status?.addEventListener?.('change', handler);
          cleanups.push(() => status?.removeEventListener?.('change', handler));
        })
        // Safari rejects the 'notifications' descriptor; the listeners below cover it.
        .catch(() => undefined);
    }
  } catch (error) {
    // Permissions API unavailable: fall through to the focus listeners.
  }

  const recheck = () => onChange(getPermissionState(), 'recheck');
  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', recheck);
  cleanups.push(() => {
    window.removeEventListener('focus', recheck);
    document.removeEventListener('visibilitychange', recheck);
  });

  return () => {
    cleanups.forEach((fn) => {
      try {
        fn();
      } catch (error) {
        // ignore teardown failures
      }
    });
  };
};

const isIOS = (ua: string): boolean => {
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports a desktop UA, so fall back to the touch-point hint.
  const nav = navigator as any;
  return nav?.platform === 'MacIntel' && (nav?.maxTouchPoints ?? 0) > 1;
};

/**
 * Detects the browser family, not the version. Only used to pick which
 * settings instructions to show, never to decide the flow itself, so a wrong
 * or spoofed user agent degrades the wording and nothing more.
 */
export const detectBrowser = (): TBrowserKey => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';

  // In-app webviews cannot receive web push and expose no settings screen,
  // so they need their own "open in a real browser" instructions.
  if (/Line\//i.test(ua) || /FBAN|FBAV|FB_IAB/i.test(ua) || /Instagram/i.test(ua)) {
    return 'inAppBrowser';
  }

  // Every iOS browser is WebKit underneath, so Chrome/Firefox on iOS follow
  // the Safari rules rather than their own desktop ones.
  if (isIOS(ua)) return 'iosSafari';

  // Order matters: Edge's user agent also contains "Chrome", and Chrome's also
  // contains "Safari". Checking the least specific first misidentifies both.
  const brands = (navigator as any).userAgentData?.brands as Array<{ brand: string }> | undefined;
  if (brands?.some((item) => /Microsoft Edge/i.test(item.brand))) return 'edge';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  // Opera, Brave and Vivaldi are Chromium based; Chrome's steps are the closest match.
  if (/OPR\/|Opera/i.test(ua)) return 'chrome';
  if (brands?.some((item) => /Google Chrome|Chromium/i.test(item.brand))) return 'chrome';
  if (/Chrome\//i.test(ua)) return 'chrome';
  if (/Safari\//i.test(ua)) return 'safari';

  return 'other';
};

/** Address-bar icons illustrated next to the first instruction step. */
export type TStepIcon = 'lock' | 'tune' | 'info' | 'blockedMessage' | 'share';

export type TInstruction = {
  titleKey: string;
  stepKeys: string[];
  /**
   * Shown beside step 1 only, since that is the step users get stuck on.
   * More than one is listed where the browser varies the icon by version or
   * connection, so the user can match whichever they actually see.
   */
  firstStepIcons?: TStepIcon[];
};

/**
 * Translation keys for the browser-specific "how to unblock" steps. Step counts
 * differ per browser, so each entry lists its own keys instead of assuming a
 * fixed number.
 */
export const getInstruction = (browser: TBrowserKey): TInstruction => {
  const instructions = translations.NOTIFICATION.INSTRUCTIONS;

  switch (browser) {
    case 'edge':
      return {
        titleKey: instructions.EDGE.TITLE,
        firstStepIcons: ['lock'],
        stepKeys: [
          instructions.EDGE.STEP_1,
          instructions.EDGE.STEP_2,
          instructions.EDGE.STEP_3,
          instructions.EDGE.STEP_4,
        ],
      };
    case 'firefox':
      return {
        titleKey: instructions.FIREFOX.TITLE,
        firstStepIcons: ['tune', 'blockedMessage'],
        stepKeys: [
          instructions.FIREFOX.STEP_1,
          instructions.FIREFOX.STEP_2,
          instructions.FIREFOX.STEP_3,
          instructions.FIREFOX.STEP_4,
        ],
      };
    case 'safari':
      return {
        titleKey: instructions.SAFARI.TITLE,
        stepKeys: [
          instructions.SAFARI.STEP_1,
          instructions.SAFARI.STEP_2,
          instructions.SAFARI.STEP_3,
          instructions.SAFARI.STEP_4,
          instructions.SAFARI.STEP_5,
          instructions.SAFARI.STEP_6,
        ],
      };
    case 'iosSafari':
      return {
        titleKey: instructions.IOS_SAFARI.TITLE,
        firstStepIcons: ['share'],
        stepKeys: [
          instructions.IOS_SAFARI.STEP_1,
          instructions.IOS_SAFARI.STEP_2,
          instructions.IOS_SAFARI.STEP_3,
          instructions.IOS_SAFARI.STEP_4,
        ],
      };
    case 'inAppBrowser':
      return {
        titleKey: instructions.IN_APP.TITLE,
        stepKeys: [
          instructions.IN_APP.STEP_1,
          instructions.IN_APP.STEP_2,
          instructions.IN_APP.STEP_3,
        ],
      };
    case 'other':
      return {
        titleKey: instructions.OTHER.TITLE,
        firstStepIcons: ['lock', 'tune', 'info'],
        stepKeys: [instructions.OTHER.STEP_1, instructions.OTHER.STEP_2, instructions.OTHER.STEP_3],
      };
    case 'chrome':
    default:
      return {
        titleKey: instructions.CHROME.TITLE,
        firstStepIcons: ['lock', 'tune', 'info'],
        stepKeys: [
          instructions.CHROME.STEP_1,
          instructions.CHROME.STEP_2,
          instructions.CHROME.STEP_3,
          instructions.CHROME.STEP_4,
        ],
      };
  }
};
